import { Link } from '@jsluna/link';
import { Form, SearchField } from '@jsluna/form';
import { ListGroup, ListGroupItem, ListGroupDropdown } from '@jsluna/list';
import { filterProps, childMatchesType, getBestKey, Portal, setHasOverlay, onKeyPress } from '@jsluna/utils';
import { Container } from '@jsluna/grid';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var HeaderItem = function HeaderItem(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      tabBar = _ref.tabBar,
      tabBarSoft = _ref.tabBarSoft,
      tabBarAlign = _ref.tabBarAlign,
      bottomBar = _ref.bottomBar,
      align = _ref.align,
      drawer = _ref.drawer,
      fullWidth = _ref.fullWidth,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "tabBar", "tabBarSoft", "tabBarAlign", "bottomBar", "align", "drawer", "fullWidth"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['closeDrawer']), {
    className: classnames('ln-c-header__item', tabBar && Array.isArray(tabBar) && tabBar.map(function (tab) {
      return "ln-c-header__item--tab-bar@" + tab;
    }).join(' '), tabBar && !Array.isArray(tabBar) && (typeof tabBar === 'string' ? "ln-c-header__item--tab-bar@" + tabBar : 'ln-c-header__item--tab-bar'), tabBarSoft && 'ln-c-header__item--tab-bar-soft', tabBarAlign && "ln-c-header__item--tab-bar-" + tabBarAlign, bottomBar && 'ln-c-header__item--bottom-bar', align && "ln-c-header__item--" + align, drawer && "ln-c-header__drawer-item ln-c-header__drawer-item--" + drawer, fullWidth && 'ln-c-header__item--full-width', className)
  }), children);
};

HeaderItem.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Whether the header item should display as a tab bar, can be fixed or responsive */
  tabBar: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.array]),

  /** Display as a mobile app style tab bar fixed to the bottom of the screen on smaller devices */
  bottomBar: PropTypes.bool,

  /** Where the item should be positioned along the header, defaults to left alignment */
  align: PropTypes.oneOf(['right', 'center']),

  /** The positioning within the drawer on smaller screen sizes */
  drawer: PropTypes.oneOfType([PropTypes.oneOf(['header', 'body', 'footer']), PropTypes.bool]),

  /** Forces the item to take up all available space within the header */
  fullWidth: PropTypes.bool,

  /** Uses a larger version of the tab bar with padding surrounding the content */
  tabBarSoft: PropTypes.bool,

  /** Set the tab bar alignment, defaults to center */
  tabBarAlign: PropTypes.oneOf(['left', 'right'])
};
HeaderItem.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  tabBar: false,
  bottomBar: false,
  align: undefined,
  drawer: false,
  fullWidth: false,
  tabBarSoft: false,
  tabBarAlign: undefined
};
HeaderItem.displayName = 'HeaderItem';
/**
 * @see Extends [HeaderItem](/#/Components/Header?id=headeritem)
 */

var HeaderSearch = function HeaderSearch(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      field = _ref.field,
      drawer = _ref.drawer,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "field", "drawer"]);

  return /*#__PURE__*/React.createElement(HeaderItem, _extends({}, rest, {
    element: element,
    className: classnames('ln-c-header__item--search', className),
    drawer: drawer === true ? 'header' : drawer,
    role: "search"
  }), /*#__PURE__*/React.createElement(SearchField, _extends({
    name: "search",
    label: "Search",
    placeholder: "Search",
    hideLabel: true
  }, field, {
    className: classnames(field.className, 'ln-c-header__search-field')
  })));
};

HeaderSearch.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Props used to customise SearchField */
  field: PropTypes.shape({
    className: PropTypes.string
  }),

  /** The positioning within the drawer on smaller screen sizes */
  drawer: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};
HeaderSearch.defaultProps = {
  element: Form,
  children: undefined,
  className: undefined,
  field: {},
  drawer: false
};
HeaderSearch.displayName = 'HeaderSearch';

var HeaderDivider = function HeaderDivider(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      size = _ref.size,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "size"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['closeDrawer']), {
    className: classnames('ln-c-header__divider', size && "ln-c-header__divider--" + size, className)
  }), children);
};

HeaderDivider.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Set sizing of spacing divider brings */
  size: PropTypes.oneOf(['small'])
};
HeaderDivider.defaultProps = {
  element: 'span',
  children: undefined,
  className: undefined,
  size: undefined
};
HeaderDivider.displayName = 'HeaderDivider';
var LIST_TYPE = {
  ACTION: 'action',
  NAV: 'nav'
};

var HeaderListGroup = function HeaderListGroup(_ref) {
  var children = _ref.children,
      className = _ref.className,
      open = _ref.open,
      hover = _ref.hover,
      openDropdownOnHover = _ref.openDropdownOnHover,
      type = _ref.type,
      listType = _ref.listType,
      onDropdownMouseEnter = _ref.onDropdownMouseEnter,
      onDropdownMouseLeave = _ref.onDropdownMouseLeave,
      onDropdownClick = _ref.onDropdownClick,
      onDropdownClose = _ref.onDropdownClose,
      closeDrawer = _ref.closeDrawer,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "open", "hover", "openDropdownOnHover", "type", "listType", "onDropdownMouseEnter", "onDropdownMouseLeave", "onDropdownClick", "onDropdownClose", "closeDrawer"]);

  var hasOpenDropdown = !!Object.keys(open).map(function (dropdown) {
    return open[dropdown];
  }).filter(Boolean).length || openDropdownOnHover && !!Object.keys(hover).map(function (dropdown) {
    return hover[dropdown];
  }).filter(Boolean).length;
  return /*#__PURE__*/React.createElement(ListGroup, _extends({}, rest, {
    className: classnames("ln-c-header__" + type + "-list", className),
    type: listType !== 'bare' ? listType : undefined
  }), React.Children.map(children, function (child, index) {
    if (childMatchesType(child, HeaderDivider)) {
      return /*#__PURE__*/React.createElement("li", {
        key: getBestKey(child.props, index),
        className: type === LIST_TYPE.NAV ? 'ln-u-display-flex' : 'ln-c-header__action-item'
      }, child);
    }

    if (childMatchesType(child, ListGroupDropdown, true)) {
      var dropdownId = child.props.id;
      var hoverBehaviour = {
        onMouseEnter: onDropdownMouseEnter(dropdownId),
        onMouseLeave: onDropdownMouseLeave(dropdownId),
        hover: hover[dropdownId]
      };
      var _actionProps = {
        containerClassName: 'ln-c-header__action-item',
        position: 'right'
      };
      return React.cloneElement(child, Object.assign({
        key: dropdownId || index,
        className: classnames(child.props.className, type === LIST_TYPE.NAV ? 'ln-c-header__nav-item' : 'ln-c-header__action-link'),
        onClick: onDropdownClick(dropdownId),
        onClose: onDropdownClose(dropdownId),
        onItemClick: closeDrawer,
        active: open[dropdownId]
      }, type === LIST_TYPE.ACTION ? _actionProps : {}, {}, openDropdownOnHover ? hoverBehaviour : {}));
    }

    var actionProps = {
      containerClassName: 'ln-c-header__action-item'
    };
    return /*#__PURE__*/React.createElement(ListGroupItem, _extends({}, child.props, {
      key: getBestKey(child.props, index),
      className: classnames(child.props.className, type === LIST_TYPE.NAV ? 'ln-c-header__nav-item' : 'ln-c-header__action-link'),
      element: child.type,
      onClick: closeDrawer
    }, type === LIST_TYPE.ACTION ? actionProps : {}), child.props.children);
  }), /*#__PURE__*/React.createElement(Portal, {
    exclusive: true,
    id: "ln-header-dropdown-overlay"
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('ln-c-header__overlay ln-c-header__overlay--dropdown', hasOpenDropdown && 'is-open'),
    "aria-hidden": "true"
  }, "\xA0")));
};

HeaderListGroup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  type: PropTypes.oneOf(Object.keys(LIST_TYPE).map(function (key) {
    return LIST_TYPE[key];
  })),

  /** Should dropdowns open on mouse over */
  openDropdownOnHover: PropTypes.bool,

  /** Object of any open dropdown id's */
  open: PropTypes.shape({}),

  /** Object of any hovered dropdown id's */
  hover: PropTypes.shape({}),

  /** handler that accepts a dropdown id and will return an event handler to
   * set the hover state to true */
  onDropdownMouseEnter: PropTypes.func,

  /** handler that accepts a dropdown id and will return an event handler to
   * set the hover state to false */
  onDropdownMouseLeave: PropTypes.func,

  /** handler that accepts a dropdown id and will return a click event
   * handler to toggle the open state */
  onDropdownClick: PropTypes.func,

  /** handler that accepts a dropdown id and will return an event handler
   * for clickaway and blur events to close the dropdown */
  onDropdownClose: PropTypes.func,

  /** Styling option used by ListGroup */
  listType: PropTypes.oneOf(['bare', 'full', 'pill']),

  /** Method use to close the drawer menu on link click */
  closeDrawer: PropTypes.func
};
HeaderListGroup.defaultProps = {
  children: undefined,
  className: undefined,
  type: LIST_TYPE.NAV,
  openDropdownOnHover: false,
  open: {},
  hover: {},
  onDropdownMouseEnter: function onDropdownMouseEnter() {},
  onDropdownMouseLeave: function onDropdownMouseLeave() {},
  onDropdownClick: function onDropdownClick() {},
  onDropdownClose: function onDropdownClose() {},
  listType: 'full',
  closeDrawer: undefined
};
HeaderListGroup.displayName = 'HeaderListGroup';
/**
 * @see Extends [HeaderItem](/#/Components/Header?id=headeritem)
 */

var HeaderNav = /*#__PURE__*/function (_Component) {
  _inheritsLoose(HeaderNav, _Component);

  function HeaderNav(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      open: {},
      hover: {},
      defaultOpen: {}
    };
    _this.handleDropdownClick = _this.handleDropdownClick.bind(_assertThisInitialized(_this));
    _this.handleDropdownClose = _this.handleDropdownClose.bind(_assertThisInitialized(_this));
    _this.handleDropdownMouseEnter = _this.handleDropdownMouseEnter.bind(_assertThisInitialized(_this));
    _this.handleDropdownMouseLeave = _this.handleDropdownMouseLeave.bind(_assertThisInitialized(_this));
    _this.setDropdownDefaultOpen = _this.setDropdownDefaultOpen.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = HeaderNav.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.setDropdownDefaultOpen();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var children = this.props.children;

    if (children !== prevProps.children) {
      this.setDropdownDefaultOpen();
    }
  };

  _proto.setDropdownDefaultOpen = function setDropdownDefaultOpen() {
    var children = this.props.children;
    var newState = {
      defaultOpen: {},
      open: {}
    };
    React.Children.map(children, function (child) {
      if (childMatchesType(child, ListGroupDropdown, true)) {
        var dropdownId = child.props.id;

        if (child.props.defaultOpen) {
          newState.defaultOpen[dropdownId] = true;
          newState.open[dropdownId] = undefined;
        } else {
          newState.defaultOpen[dropdownId] = false;
        }
      }
    });

    if (Object.keys(newState.defaultOpen).length) {
      this.setState(newState);
    }
  };

  _proto.handleDropdownClick = function handleDropdownClick(id) {
    var _this2 = this;

    return function (e) {
      var _this2$state = _this2.state,
          openState = _this2$state.open,
          defaultOpen = _this2$state.defaultOpen;
      var dropdownState = openState[id];

      if (typeof dropdownState === 'undefined') {
        var _open;

        _this2.setState({
          open: (_open = {}, _open[id] = !defaultOpen[id], _open)
        });
      } else {
        var _open2;

        _this2.setState({
          open: (_open2 = {}, _open2[id] = !dropdownState, _open2)
        });
      }

      e.preventDefault();
    };
  };

  _proto.handleDropdownClose = function handleDropdownClose(id) {
    var _this3 = this;

    return function () {
      var _open3;

      _this3.setState({
        open: (_open3 = {}, _open3[id] = false, _open3)
      });
    };
  };

  _proto.handleDropdownMouseEnter = function handleDropdownMouseEnter(id) {
    var _this4 = this;

    return function () {
      var _hover;

      _this4.setState({
        hover: (_hover = {}, _hover[id] = true, _hover)
      });
    };
  };

  _proto.handleDropdownMouseLeave = function handleDropdownMouseLeave(id) {
    var _this5 = this;

    return function () {
      var _hover2;

      _this5.setState({
        hover: (_hover2 = {}, _hover2[id] = false, _hover2)
      });
    };
  };

  _proto.render = function render() {
    var _this$props = this.props,
        element = _this$props.element,
        children = _this$props.children,
        className = _this$props.className,
        label = _this$props.label,
        tabBar = _this$props.tabBar,
        bottomBar = _this$props.bottomBar,
        drawer = _this$props.drawer,
        type = _this$props.type,
        size = _this$props.size,
        spaced = _this$props.spaced,
        color = _this$props.color,
        openDropdownOnHover = _this$props.openDropdownOnHover,
        closeDrawer = _this$props.closeDrawer,
        rest = _objectWithoutPropertiesLoose(_this$props, ["element", "children", "className", "label", "tabBar", "bottomBar", "drawer", "type", "size", "spaced", "color", "openDropdownOnHover", "closeDrawer"]);

    var _this$state = this.state,
        open = _this$state.open,
        hover = _this$state.hover;
    return /*#__PURE__*/React.createElement(HeaderItem, _extends({}, rest, {
      element: element,
      className: classnames('ln-c-header__item--nav', className),
      tabBar: tabBar,
      bottomBar: bottomBar,
      drawer: drawer === true ? 'body' : drawer,
      "aria-label": label,
      role: element !== 'nav' ? 'navigation' : undefined
    }), /*#__PURE__*/React.createElement(HeaderListGroup, {
      listType: type,
      color: color,
      size: size,
      inline: bottomBar || tabBar === true || 'nav',
      actionable: true,
      spaced: spaced,
      open: open,
      hover: hover,
      openDropdownOnHover: openDropdownOnHover,
      onDropdownMouseEnter: this.handleDropdownMouseEnter,
      onDropdownMouseLeave: this.handleDropdownMouseLeave,
      onDropdownClick: this.handleDropdownClick,
      onDropdownClose: this.handleDropdownClose,
      closeDrawer: closeDrawer
    }, children));
  };

  return HeaderNav;
}(Component);

HeaderNav.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Aria label used to give greater context of navigation to screen reader users */
  label: PropTypes.string,

  /** Whether the nav should display as a tab bar, can be fixed or responsive */
  tabBar: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** The positioning within the drawer on smaller screen sizes */
  drawer: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** Display as a mobile app style tab bar fixed to the bottom of the screen on smaller devices */
  bottomBar: PropTypes.bool,

  /** Styling option used by ListGroup */
  type: PropTypes.oneOf(['bare', 'full', 'pill']),

  /** Size of ListGroupItems */
  size: PropTypes.string,

  /** Whether navigation items should have spacing between them */
  spaced: PropTypes.bool,

  /** Color option used by ListGroup */
  color: PropTypes.string,

  /** Should dropdowns open on mouse over */
  openDropdownOnHover: PropTypes.bool,

  /** Method use to close the drawer menu on link click */
  closeDrawer: PropTypes.func
};
HeaderNav.defaultProps = {
  element: 'nav',
  children: undefined,
  className: undefined,
  label: undefined,
  tabBar: false,
  drawer: undefined,
  bottomBar: false,
  type: 'full',
  size: undefined,
  spaced: false,
  color: undefined,
  openDropdownOnHover: false,
  closeDrawer: undefined
};
HeaderNav.displayName = 'HeaderNav';
/**
 * @see Extends [HeaderItem](/#/Components/Header?id=headeritem)
 */

var HeaderActions = /*#__PURE__*/function (_HeaderNav) {
  _inheritsLoose(HeaderActions, _HeaderNav);

  function HeaderActions() {
    return _HeaderNav.apply(this, arguments) || this;
  }

  var _proto = HeaderActions.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        element = _this$props.element,
        children = _this$props.children,
        className = _this$props.className,
        label = _this$props.label,
        drawer = _this$props.drawer,
        openDropdownOnHover = _this$props.openDropdownOnHover,
        rest = _objectWithoutPropertiesLoose(_this$props, ["element", "children", "className", "label", "drawer", "openDropdownOnHover"]);

    var _this$state = this.state,
        open = _this$state.open,
        hover = _this$state.hover;
    return /*#__PURE__*/React.createElement(HeaderItem, _extends({}, rest, {
      element: element,
      className: classnames('ln-c-header__item--action', className),
      drawer: drawer === true ? 'header' : drawer,
      "aria-label": label,
      role: element !== 'nav' ? 'navigation' : undefined
    }), /*#__PURE__*/React.createElement(HeaderListGroup, {
      type: "action",
      listType: "bare",
      inline: true,
      hard: true,
      spaced: true,
      open: open,
      hover: hover,
      openDropdownOnHover: openDropdownOnHover,
      onDropdownMouseEnter: this.handleDropdownMouseEnter,
      onDropdownMouseLeave: this.handleDropdownMouseLeave,
      onDropdownClick: this.handleDropdownClick,
      onDropdownClose: this.handleDropdownClose
    }, children));
  };

  return HeaderActions;
}(HeaderNav);

HeaderActions.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Aria label used to give greater context of navigation to screen reader users */
  label: PropTypes.string,

  /** Whether the nav should appear in the drawer on small screens */
  drawer: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** Should dropdowns open on mouse over */
  openDropdownOnHover: PropTypes.bool
};
HeaderActions.defaultProps = {
  element: 'nav',
  children: undefined,
  className: undefined,
  label: undefined,
  drawer: false,
  openDropdownOnHover: false
};
HeaderActions.displayName = 'HeaderActions';

var HeaderTabBar = function HeaderTabBar(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      soft = _ref.soft,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "soft"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['closeDrawer']), {
    className: classnames('ln-c-header__tab-bar', soft && 'ln-c-header__tab-bar--soft', className)
  }), children);
};

HeaderTabBar.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,
  soft: PropTypes.bool
};
HeaderTabBar.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  soft: false
};
HeaderTabBar.displayName = 'HeaderTabBar';

var HeaderMainBar = /*#__PURE__*/function (_Component) {
  _inheritsLoose(HeaderMainBar, _Component);

  function HeaderMainBar(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    var onToggle = props.onToggle;
    _this.state = {
      hasBottomBar: false,
      hasTabBar: [],
      hasDrawer: false
    };
    _this.parseChildren = _this.parseChildren.bind(_assertThisInitialized(_this));

    _this.closeDrawer = function (e) {
      return onToggle(e, false);
    };

    return _this;
  }

  var _proto = HeaderMainBar.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.parseChildren();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var children = this.props.children;

    if (children !== prevProps.children) {
      this.parseChildren();
    }
  };

  _proto.parseChildren = function parseChildren() {
    var children = this.props.children;
    var hasTabBar = [];
    var hasBottomBar = false;
    var hasDrawer = false;
    React.Children.forEach(children, function (child) {
      if (childMatchesType(child, [HeaderItem, HeaderSearch, HeaderNav, HeaderActions])) {
        if (child.props.tabBar && !hasTabBar.includes(child.props.tabBar)) {
          hasTabBar.push([child.props.tabBar, child.props.tabBarSoft]);
        }

        if (child.props.bottomBar) {
          hasBottomBar = true;
        }

        if (child.props.drawer) {
          hasDrawer = true;
        }
      } else if (childMatchesType(child, HeaderTabBar)) {
        hasTabBar.push(true);
      }
    });
    this.setState({
      hasTabBar: hasTabBar,
      hasBottomBar: hasBottomBar,
      hasDrawer: hasDrawer
    });
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$state = this.state,
        hasBottomBar = _this$state.hasBottomBar,
        hasTabBar = _this$state.hasTabBar,
        hasDrawer = _this$state.hasDrawer;

    var _this$props = this.props,
        children = _this$props.children,
        className = _this$props.className,
        onToggle = _this$props.onToggle,
        isSticky = _this$props.isSticky,
        isStuck = _this$props.isStuck,
        forceGlobalBar = _this$props.hasGlobalBar,
        forceBottomBar = _this$props.hasBottomBar,
        forceTabBar = _this$props.hasTabBar,
        forceDrawer = _this$props.hasDrawer,
        rest = _objectWithoutPropertiesLoose(_this$props, ["children", "className", "onToggle", "isSticky", "isStuck", "hasGlobalBar", "hasBottomBar", "hasTabBar", "hasDrawer"]);

    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Container, _extends({
      className: classnames('ln-c-header__main-bar', className),
      soft: true
    }, rest), hasDrawer && /*#__PURE__*/React.createElement("button", {
      className: "ln-c-header__toggle ln-c-header-toggle",
      type: "button",
      onClick: onToggle
    }, /*#__PURE__*/React.createElement("span", {
      className: "ln-c-header-toggle__item"
    }), /*#__PURE__*/React.createElement("span", {
      className: "ln-u-visually-hidden"
    }, "Menu")), React.Children.map(children, function (child) {
      return React.isValidElement(child) ? React.cloneElement(child, {
        closeDrawer: _this2.closeDrawer
      }) : child;
    })), /*#__PURE__*/React.createElement(Portal, {
      prepend: true,
      exclusive: true,
      id: "ln-header-spacer",
      selector: ".ln-o-page"
    }, !isSticky && /*#__PURE__*/React.createElement("div", {
      "aria-hidden": true,
      className: "ln-c-header__spacer ln-c-header__spacer--collapsed"
    }, "\xA0"), isSticky && forceGlobalBar && /*#__PURE__*/React.createElement("div", {
      "aria-hidden": true,
      className: classnames('ln-c-header__spacer', 'ln-c-header__spacer--global-bar', isStuck && 'is-stuck')
    }, "\xA0"), forceTabBar && isSticky && /*#__PURE__*/React.createElement("div", {
      className: classnames('ln-c-header__spacer', forceTabBar === true ? 'ln-c-header__spacer--tab-bar' : "ln-c-header__spacer--tab-bar@" + forceTabBar)
    }, "\xA0"), hasTabBar.length && isSticky ? hasTabBar.map(function (_ref) {
      var tab = _ref[0],
          soft = _ref[1];
      return /*#__PURE__*/React.createElement("div", {
        key: tab.toString(),
        className: classnames('ln-c-header__spacer', Array.isArray(tab) ? tab.map(function (tabType) {
          return [tab === true ? 'ln-c-header__spacer--tab-bar' : "ln-c-header__spacer--tab-bar@" + tabType, soft && 'ln-c-header__spacer--tab-bar-soft'];
        }) : [tab === true ? 'ln-c-header__spacer--tab-bar' : "ln-c-header__spacer--tab-bar@" + tab, soft && 'ln-c-header__spacer--tab-bar-soft'])
      }, "\xA0");
    }) : /*#__PURE__*/React.createElement("div", {
      className: classnames(hasTabBar.map(function (tab) {
        return (tab === 'max-nav' || tab === true) && 'ln-c-header__spacer ln-c-header__spacer--tab-bar@max-nav';
      })) || 'ln-u-display-none'
    }, "\xA0")), (hasBottomBar || forceBottomBar) && /*#__PURE__*/React.createElement(Portal, {
      exclusive: true,
      id: "ln-header-bottom-bar-spacer",
      selector: ".ln-o-page"
    }, /*#__PURE__*/React.createElement("div", {
      hidden: true,
      "aria-hidden": true,
      className: "ln-c-header__spacer ln-c-header__spacer--bottom-bar"
    }, "\xA0")));
  };

  return HeaderMainBar;
}(Component);

HeaderMainBar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,

  /** Function to open and close the drawer menu on small screens */
  onToggle: PropTypes.func,

  /** Whether the header should stick to the top of the page */
  isSticky: PropTypes.bool,

  /** Flags if the user has scrolled to trigger style changes to header */
  isStuck: PropTypes.bool,

  /** Flag to force knowledge of global bar for correct spacing if fixed, should be detectable in most circumstances */
  hasGlobalBar: PropTypes.bool,

  /** Flag to force knowledge of bottom bar for correct spacing if fixed, should be detectable in most circumstances */
  hasBottomBar: PropTypes.bool,

  /** Flag to force knowledge of tab bar for correct spacing if fixed, should be detectable in most circumstances */
  hasTabBar: PropTypes.bool,

  /** Flag to force knowledge of drawer items to display toggle button, should be detectable in most circumstances */
  hasDrawer: PropTypes.bool
};
HeaderMainBar.defaultProps = {
  children: undefined,
  className: undefined,
  onToggle: undefined,
  isSticky: false,
  isStuck: false,
  hasGlobalBar: false,
  hasBottomBar: false,
  hasTabBar: false,
  hasDrawer: false
};
HeaderMainBar.displayName = 'HeaderMainBar';

var GlobalBar = function GlobalBar(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "label"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-header__global-bar', className),
    "aria-label": label
  }), /*#__PURE__*/React.createElement(Container, {
    className: "ln-c-header__global-bar-inner"
  }, children));
};

GlobalBar.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Aria label used to give greater context of navigation to screen reader users */
  label: PropTypes.string
};
GlobalBar.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  label: undefined
};
GlobalBar.displayName = 'HeaderGlobalBar';

var Header = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Header, _Component);

  function Header(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      open: false,
      parsedChildren: false
    };
    _this.handleClickAway = _this.handleClickAway.bind(_assertThisInitialized(_this));
    _this.handleToggleClick = _this.handleToggleClick.bind(_assertThisInitialized(_this));
    _this.parseChildren = _this.parseChildren.bind(_assertThisInitialized(_this));
    _this.onKeyPress = onKeyPress([{
      name: 'Escape',
      code: 27
    }], function (e) {
      _this.handleEscapePress(e);
    });
    return _this;
  }

  var _proto = Header.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.parseChildren();
    document.addEventListener('keydown', this.onKeyPress, false);
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var children = this.props.children;

    if (children !== prevProps.children) {
      this.parseChildren();
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var open = this.state.open;
    var setHasOverlay$$1 = this.props.setHasOverlay;

    if (open) {
      setHasOverlay$$1(false);
    }

    document.removeEventListener('keydown', this.onKeyPress, false);
  };

  _proto.handleToggleClick = function handleToggleClick(e, forceState) {
    var openState = this.state.open;
    var setHasOverlay$$1 = this.props.setHasOverlay;
    var open = typeof forceState !== 'undefined' ? forceState : !openState;
    this.setState({
      open: open
    });
    setHasOverlay$$1(open);
  };

  _proto.handleClickAway = function handleClickAway(e) {
    this.handleToggleClick(e, false);
  };

  _proto.handleEscapePress = function handleEscapePress(e) {
    var open = this.state.open;

    if (open) {
      this.handleToggleClick(e, false);
    }
  };

  _proto.parseChildren = function parseChildren() {
    var children = this.props.children;
    var hasHeaderMainBar = false;
    var hasGlobalBar = false;
    var hasTabBar = false;
    React.Children.forEach(children, function (child) {
      if (childMatchesType(child, HeaderMainBar)) {
        hasHeaderMainBar = true;
      }

      if (childMatchesType(child, GlobalBar)) {
        hasGlobalBar = true;
      }

      if (childMatchesType(child, HeaderTabBar)) {
        hasTabBar = true;
      }
    });
    this.setState({
      hasHeaderMainBar: hasHeaderMainBar,
      hasGlobalBar: hasGlobalBar,
      hasTabBar: hasTabBar,
      parsedChildren: true
    });
  };

  _proto.render = function render() {
    var _this$state = this.state,
        open = _this$state.open,
        hasDetectedHeaderMainBar = _this$state.hasHeaderMainBar,
        hasGlobalBar = _this$state.hasGlobalBar,
        hasTabBar = _this$state.hasTabBar,
        parsedChildren = _this$state.parsedChildren;

    var _this$props = this.props,
        element = _this$props.element,
        children = _this$props.children,
        className = _this$props.className,
        mainId = _this$props.mainId,
        isSticky = _this$props.isSticky,
        isStuck = _this$props.isStuck,
        forceHasGlobalBar = _this$props.hasGlobalBar,
        forceHasBottomBar = _this$props.hasBottomBar,
        forceHasTabBar = _this$props.hasTabBar,
        forceHasMainBar = _this$props.hasMainBar,
        hasDrawer = _this$props.hasDrawer,
        divided = _this$props.divided,
        rest = _objectWithoutPropertiesLoose(_this$props, ["element", "children", "className", "mainId", "isSticky", "isStuck", "hasGlobalBar", "hasBottomBar", "hasTabBar", "hasMainBar", "hasDrawer", "divided"]);

    var hasHeaderMainBar = forceHasMainBar || hasDetectedHeaderMainBar;
    var Element = element;
    var headerMainBarProps = {
      onToggle: this.handleToggleClick,
      isSticky: isSticky,
      isStuck: isStuck,
      hasGlobalBar: forceHasGlobalBar || hasGlobalBar,
      hasBottomBar: forceHasBottomBar,
      hasTabBar: forceHasTabBar || hasTabBar,
      hasDrawer: hasDrawer
    };
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Link, {
      href: "#" + mainId,
      color: "dark",
      skip: true
    }, "Skip to content"), /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['setHasOverlay']), {
      className: classnames('ln-c-header', hasGlobalBar && 'ln-c-header--has-global-bar', hasTabBar && 'ln-c-header--has-tab-bar', divided && 'ln-c-header--divided', open && 'is-open', isSticky && 'is-sticky', isStuck && 'is-stuck', className)
    }), (parsedChildren || forceHasMainBar) && (hasHeaderMainBar ? React.Children.map(children, function (child) {
      return childMatchesType(child, HeaderMainBar) ? React.cloneElement(child, headerMainBarProps) : child;
    }) : /*#__PURE__*/React.createElement(HeaderMainBar, headerMainBarProps, children)), /*#__PURE__*/React.createElement("div", {
      className: "ln-c-header__overlay ln-c-header__overlay--drawer",
      "aria-hidden": true,
      onClick: this.handleClickAway
    })));
  };

  return Header;
}(Component);

Header.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** ID used for the skip link to jump to page's main content */
  mainId: PropTypes.string,

  /** Whether the header should stick to the top of the page */
  isSticky: PropTypes.bool,

  /** Flags if the user has scrolled to trigger style changes to header */
  isStuck: PropTypes.bool,

  /** Prop to override the setting of the `has-overlay` class on the `body` element which prevents scolling of content behind the modal */
  setHasOverlay: PropTypes.func,

  /** Flag to force knowledge of global bar for correct spacing if fixed, should be detectable in most circumstances */
  hasGlobalBar: PropTypes.bool,

  /** Flag to force knowledge of bottom bar for correct spacing if fixed, should be detectable in most circumstances */
  hasBottomBar: PropTypes.bool,

  /** Flag to force knowledge of tab bar for correct spacing if fixed, should be detectable in most circumstances */
  hasTabBar: PropTypes.bool,

  /** Flag to force knowledge of main bar for correct element nesting, should be detectable in most circumstances */
  hasMainBar: PropTypes.bool,

  /** Flag to force knowledge of drawer items to display toggle button, should be detectable in most circumstances */
  hasDrawer: PropTypes.bool,

  /** Add borders between bars within the header */
  divided: PropTypes.bool
};
Header.defaultProps = {
  element: 'header',
  children: undefined,
  className: undefined,
  mainId: 'main-content',
  isSticky: false,
  isStuck: false,
  setHasOverlay: setHasOverlay,
  hasGlobalBar: false,
  hasBottomBar: false,
  hasTabBar: false,
  hasMainBar: false,
  hasDrawer: false,
  divided: false
};
Header.displayName = 'Header';

var GlobalItem = function GlobalItem(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      align = _ref.align,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "align"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-header__global-item', align && "ln-c-header__global-item--" + align, className)
  }), children);
};

GlobalItem.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** Where the item should be placed along the global bar, defaults to left alignment */
  align: PropTypes.oneOf(['right', 'center'])
};
GlobalItem.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  align: undefined
};
GlobalItem.displayName = 'HeaderGlobalItem';
/**
 * @see Extends [HeaderItem](/#/Components/Header?id=headeritem)
 */

var HeaderLogo = function HeaderLogo(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  return /*#__PURE__*/React.createElement(HeaderItem, _extends({}, rest, {
    className: classnames('ln-c-header__item--logo', className)
  }), children);
};

HeaderLogo.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};
HeaderLogo.defaultProps = {
  children: undefined,
  className: undefined
};
HeaderLogo.displayName = 'HeaderLogo';
export { Header, HeaderActions, HeaderDivider, GlobalBar as HeaderGlobalBar, GlobalItem as HeaderGlobalItem, HeaderMainBar, HeaderItem, HeaderListGroup, HeaderLogo, HeaderNav, HeaderSearch, HeaderTabBar };